import { Box, Typography, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { ECElementEvent, EChartsOption, TitleComponentOption } from 'echarts';
import { GraphicComponentOption } from 'echarts/components';

// Combine necessary option types
type ECOption = EChartsOption & {
  graphic?: GraphicComponentOption[];
  title?: TitleComponentOption | TitleComponentOption[];
};

type SeriesItem = {
  name: string;
  value: number;
  unit: 'GB' | 'TB' | 'PB';
  itemStyle?: {
    color: string;
  };
};

type DoughnutChartWidgetProps = {
  isLoading?: boolean;
  totalStorage?: string;
  changeText?: string;
  seriesData?: SeriesItem[];
  onSliceClick?: (params: ECElementEvent) => void;
  title?: any;
};

function formatNumberByUnits(value: number): string {
  if (value >= 1e12) return (value / 1e12).toFixed(2) + ' TB';
  if (value >= 1e9) return (value / 1e9).toFixed(2) + ' GB';
  if (value >= 1e6) return (value / 1e6).toFixed(2) + ' MB';
  return value.toString();
}

const getGridLeft = (width: number): string => {
  if (width < 420) return '18%';
  if (width < 600) return '15%';
  if (width < 820) return '12%';
  if (width < 920) return '9%';
  if (width < 1260) return '7%';
  return '5%';
};

const DoughnutChartWidget = ({
  isLoading = false,
  totalStorage,
  changeText,
  seriesData = [],
  onSliceClick,
  title
}: DoughnutChartWidgetProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [, setGridLeft] = useState('5%');

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartRef.current) {
        const width = chartRef.current.offsetWidth;
        const left = getGridLeft(width);
        setGridLeft(left);
        setWindowSize(window.innerWidth);
      }
    }, 200);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const chartOptions: ECOption = seriesData.length === 0
    ? {
        graphic: [
          { type: 'circle', left: 'center', top: 'center', shape: { r: 100 }, style: { fill: '#71758f66' } },
          { type: 'circle', left: 'center', top: 'center', shape: { r: 60 }, style: { fill: '#053367' } }
        ],
        title: {
          show: true,
          text: 'No data',
          left: 'center',
          top: 'center',
          textStyle: { color: '#bcbcbc', fontSize: 16, fontWeight: 'normal' }
        },
        backgroundColor: '#053367'
      }
    : {
        tooltip: {
          backgroundColor: '#003771',
          textStyle: { color: '#A9ADCB' },
          formatter: function (params: any) {
            const value = params.value;
            return `${params.name} : ${params.percent}% (${value} ${params.data.unit})`;
          },
          position: 'inside'
        },
        legend: {
          show: false
        },
        series: [
          {
            type: 'pie',
            radius: ['60%', '80%'],
            avoidLabelOverlap: false,
            label: { show: false },
            data: seriesData
          }
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '40%',
            style: {
              text: 'Total Storage',
              fill: '#F5F5F5',
              fontSize: 14,
              fontWeight: 'normal'
            }
          },
          {
            type: 'text',
            left: 'center',
            top: '50%',
            style: {
              text: totalStorage,
              fill: '#F5F5F5',
              fontSize: String(totalStorage).length >= 9 ? 22 : 32,
              fontWeight: 'bold'
            }
          },
          // {
          //   type: 'text',
          //   left: 'center',
          //   top: '60%',
          //   style: {
          //     text: changeText ? changeText : undefined,
          //     fill: '#D82B51',
          //     fontSize: 14,
          //     fontWeight: 500
          //   }
          // },
          // {
          //   type: 'text',
          //   left: 'center',
          //   top: '65%',
          //   style: {
          //     text: 'vs Last Month',
          //     fill: '#D82B51',
          //     fontSize: 14,
          //     fontWeight: 500
          //   }
          // }
        ]
      };

  return (
		<Box display="flex" flexGrow={1} height="100%" position="relative">
			<Stack p={2} bgcolor="primary.main" fontFamily="Montserrat" borderRadius="5px" flexGrow={1}>
				<Typography sx={{ fontSize: { xs: '18px', lg: '20px' } }} fontWeight={500} color="#FFFFFF">
					{title}
				</Typography>

				<Stack key={windowSize} ref={chartRef} flexGrow={1} justifyContent="center" minHeight="300px">
					{!isLoading ? (
						<ReactECharts
							option={chartOptions}
							style={{ width: '100%', height: '100%' }}
							opts={{ renderer: 'canvas', width: 'auto' }}
							notMerge={true}
							lazyUpdate={true}
							onEvents={{ click: (params: ECElementEvent) => onSliceClick?.(params) }}
						/>
					) : (
						<LoaderComponent />
					)}
				</Stack>

				{/* <Box display="flex" justifyContent="center">
					<Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
						<Typography
							fontSize={{ xs: '14px', sm: '16px', md: '18px' }}
							fontWeight={600}
							color="#009BD8"
							sx={{ display: 'flex', alignItems: 'center' }}
						>
							View Storage By Technologies
							<ChevronRightIcon sx={{ ml: 0.5, color: '#009BD8' }} />
						</Typography>
					</Box>
				</Box> */}
			</Stack>
		</Box>
	);
};

export default DoughnutChartWidget;
